import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileText, BarChart3, ClipboardList, Leaf, Sun, Sprout, Handshake, ExternalLink } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { TenantShell, BrandButton, STEPS } from "../../components/tenant/TenantShell";

const ICONS = [ClipboardList, FileText, BarChart3, CheckCircle2];
const ECO_STEPS = [
  { n: 1, title: "Confirm your sector", Icon: Leaf },
  { n: 2, title: "Tell us about your business", Icon: ClipboardList },
  { n: 3, title: "Upload your documents & get scored", Icon: BarChart3 },
  { n: 4, title: "Get matched to funders & accelerators", Icon: Handshake },
];
const SECTOR_ICONS = { climate: Leaf, nature: Sprout, energy: Sun };

function StepList({ steps, testid }) {
  return (
    <ol className="space-y-3" data-testid={testid}>
      {steps.map((s) => (
        <li key={s.n} className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: "var(--tenant)" }}><s.Icon size={20} /></span>
          <div><p className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">Step {s.n}</p><p className="text-sm font-bold">{s.title}</p></div>
        </li>
      ))}
    </ol>
  );
}

function EcosystemLanding() {
  const { config, me, to } = useTenant();
  const navigate = useNavigate();
  const start = () => navigate(to(me ? "/journey" : "/fit"));
  const sectors = config.focus_sectors || [];
  return (
    <section className="grid items-center gap-10 py-6 lg:grid-cols-[1.2fr_1fr]">
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }} data-testid="tenant-eyebrow">
          {config.name} × Clinq · Ecosystem Partner
        </p>
        <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="tenant-headline">{config.headline}</h1>
        <p className="mt-5 max-w-xl text-base text-neutral-600" data-testid="tenant-subline">{config.subline}</p>
        {sectors.length > 0 && (
          <ul className="mt-5 flex flex-wrap gap-2" data-testid="tenant-focus-sectors">
            {sectors.map((s) => { const Icon = SECTOR_ICONS[s.toLowerCase()] || Leaf; return (
              <li key={s} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold capitalize" style={{ borderColor: "var(--tenant)", color: "var(--tenant)" }}><Icon size={12} /> {s}</li>
            ); })}
          </ul>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <BrandButton onClick={start} data-testid="tenant-start-btn">{me ? "Continue my assessment" : "Check if my business fits"} <ArrowRight size={16} /></BrandButton>
          <span className="text-xs text-neutral-500">Free · Confidential · About 10 minutes</span>
        </div>
        {config.website_url && (
          <a href={config.website_url} target="_blank" rel="noreferrer" data-testid="tenant-website-link"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold underline-offset-4 hover:underline" style={{ color: "var(--tenant)" }}>
            Learn more about what we do at {config.name} <ExternalLink size={14} />
          </a>
        )}
      </div>
      <StepList steps={ECO_STEPS} testid="tenant-landing-steps" />
    </section>
  );
}

function LenderLanding() {
  const { config, me, to } = useTenant();
  const navigate = useNavigate();
  const start = () => navigate(to(me ? "/journey" : config.prequal_flow ? "/prequalify" : "/start"));
  const steps = STEPS.map((s, i) => ({ ...s, Icon: ICONS[i] }));
  return (
    <section className="grid items-center gap-10 py-6 lg:grid-cols-[1.2fr_1fr]">
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }} data-testid="tenant-eyebrow">{config.eyebrow || `${config.name} · SME Lending`}</p>
        <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="tenant-headline">{config.headline}</h1>
        <p className="mt-5 max-w-xl text-base text-neutral-600" data-testid="tenant-subline">
          {config.subline || `A free, confidential assessment. Tell us about your business, upload your records, and get a clear answer on whether you meet ${config.name}'s lending criteria — plus an indicative facility size if you do.`}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <BrandButton onClick={start} data-testid="tenant-start-btn">{me ? "Continue my assessment" : (config.cta || "Start my assessment")} <ArrowRight size={16} /></BrandButton>
          <span className="text-xs text-neutral-500">Takes about 10 minutes · No fee</span>
        </div>
      </div>
      <StepList steps={steps} testid="tenant-landing-steps" />
    </section>
  );
}

export default function TenantLanding() {
  const { config } = useTenant();
  return (
    <TenantShell wide>
      {config.partner_type === "ecosystem" ? <EcosystemLanding /> : <LenderLanding />}
    </TenantShell>
  );
}
